import { Component, Renderer2, Inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { RelevantWhitepaperComponent } from '../relevant-whitepaper/relevant-whitepaper.component';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ResourcesService } from '../../../services/resources.service';
import { HttpClientModule } from '@angular/common/http';

@Component({
  selector: 'app-agentic-ai-in-sdlc',
  imports: [CommonModule, RelevantWhitepaperComponent, ReactiveFormsModule, HttpClientModule],
  standalone: true,
  providers: [ResourcesService],
  templateUrl: './agentic-ai-in-sdlc.component.html',
  styleUrl: './agentic-ai-in-sdlc.component.scss'
})
export class AgenticAiInSdlcComponent implements OnInit, OnDestroy {
  isModalOpen: boolean = false;
  downloadForm: FormGroup;
  isSubmitting: boolean = false;
  submitSuccess: boolean = false;
  errorMessage: string = '';
  private jsonLdScript: any;

  // IMPORTANT: This string matches the Key added to the Dictionary in EmailService.cs
  readonly whitepaperTitle = "Agentic AI in SDLC";

  // Path for direct browser download (optional, if you want them to get it immediately + email)
  readonly pdfPath = "../../../../assets/resources/whitepaper/agentic-ai-in-sdlc/agentic-ai-in-sdlc.pdf";

  constructor(
    private fb: FormBuilder,
    private resourcesService: ResourcesService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) {
    this.downloadForm = this.fb.group({
      name: ['', [Validators.required, Validators.pattern('^[A-Za-z]+( [A-Za-z]+)*$')]], // Added pattern for consistency with apply-position
      email: ['', [Validators.required, Validators.pattern('^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{3,4}$')]]
    });
  }
  ngOnInit(): void {
    this.injectSchemaMarkup();
  }

  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }

  injectSchemaMarkup(): void {
    const agenticGuideSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/agentic-ai",
          "url": "https://www.kalpitatechnologies.com/tech-blogs/agentic-ai",
          "name": "A Comprehensive Guide to Agentic AI: Components, Implementation, and Examples",
          "description": "Comprehensive 2025 guide to Agentic AI covering perception, reasoning, action, implementation strategies, and real-world examples across healthcare, finance, and logistics.",
          "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": [".padding-headningmain", ".agenticai-heading", ".container-fluid p:first-of-type"]
          },
          "significantLink": [
            "https://www.functionize.com/blog/gartner-top-strategic-trends-2025",
            "https://deepmind.google/discover/blog/codoc-developing-reliable-ai-tools-for-healthcare/",
            "https://www.intuitive.com/en-us/patients/da-vinci-robotic-surgery"
          ],
          "datePublished": "2025-01-28",
          "dateModified": "2025-01-28",
          "isPartOf": { "@type": "WebSite", "@id": "https://www.kalpitatechnologies.com/#website" }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/agentic-ai#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.kalpitatechnologies.com/" },
            { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://www.kalpitatechnologies.com/tech-blogs/blog" },
            { "@type": "ListItem", "position": 3, "name": "Agentic AI Guide", "item": "https://www.kalpitatechnologies.com/tech-blogs/agentic-ai" }
          ]
        },
        {
          "@type": "BlogPosting",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/agentic-ai#article",
          "headline": "A Comprehensive Guide to Agentic AI: Components, Implementation, and Examples",
          "articleBody": "Agentic AI is rapidly emerging as a transformative technology... consisting of four interconnected components: Perception, Reasoning, Action, and Feedback and Learning...",
          "keywords": ["agentic AI", "AI agents", "autonomous AI systems", "AI implementation", "explainable AI"],
          "author": { "@type": "Person", "name": "Nagaraj Mantha" },
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
          },
          "datePublished": "2025-01-28",
          "image": "https://www.kalpitatechnologies.com/assets/resources/blog/agentic-ai/agentic-banner.png"
        },
        {
          "@type": "HowTo",
          "name": "How to Implement Agentic AI in Real-World Systems",
          "description": "Step-by-step guide for building and deploying Agentic AI.",
          "step": [
            { "@type": "HowToStep", "name": "Define the Problem", "text": "Outline objectives and KPIs the AI system should achieve." },
            { "@type": "HowToStep", "name": "Train Models", "text": "Use datasets to train models that interpret environments." },
            { "@type": "HowToStep", "name": "Real-World Deployment", "text": "Roll out to production in increments." },
            { "@type": "HowToStep", "name": "Improve", "text": "Use feedback loops to refine the system." }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is Agentic AI?",
              "acceptedAnswer": { "@type": "Answer", "text": "AI systems that behave like agents capable of perceiving environment, making decisions, and taking action." }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "address": [
            {
              "@type": "PostalAddress",
              "streetAddress": "Suite #300, 3rd Floor, Building 2, 1250 South Capital of Texas Highway",
              "addressLocality": "Austin",
              "addressRegion": "TX",
              "postalCode": "78746",
              "addressCountry": "US"
            }
          ]
        },
        {
          "@type": "LocalBusiness",
          "@id": "https://www.kalpitatechnologies.com/#localbusiness",
          "name": "Kalpita Technologies — Austin Office",
          "telephone": "+1-512-240-2484",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Suite #300, 3rd Floor, Building 2, 1250 South Capital of Texas Highway",
            "addressLocality": "Austin",
            "addressRegion": "TX",
            "postalCode": "78746",
            "addressCountry": "US"
          }
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(agenticGuideSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  openModal() {
    this.isModalOpen = true;
    this.submitSuccess = false;
    this.errorMessage = '';
    this.isSubmitting = false;
    this.downloadForm.reset();
  }

  closeModal() {
    this.isModalOpen = false;
  }

  onSubmit() {
    if (this.downloadForm.valid) {
      this.isSubmitting = true;
      this.errorMessage = '';

      // Construct Payload matching C# WhitepaperDownloadForm
      const payload = {
        Name: this.downloadForm.value.name,
        Email: this.downloadForm.value.email,
        WhitepaperTitle: this.whitepaperTitle
      };

      this.resourcesService.sendWhitepaperEmail(payload).subscribe({
        next: (response: any) => {
          // Check backend response structure. Usually returns { IsError: false, data: true }
          if (response && !response.IsError) {
            this.handleSuccess();
          } else {
            this.isSubmitting = false;
            this.errorMessage = response?.Message || 'Failed to send email. Please try again.';
          }
        },
        error: (error) => {
          this.isSubmitting = false;
          console.error('Error sending email:', error);
          this.errorMessage = 'Server error. Please check your internet connection.';
        }
      });
    } else {
      this.downloadForm.markAllAsTouched();
    }
  }

  handleSuccess() {
    this.isSubmitting = false;
    this.submitSuccess = true;

    // OPTIONAL: Trigger automatic browser download alongside email
    // this.triggerFileDownload(); 

    // Close modal after 4 seconds
    setTimeout(() => {
      this.closeModal();
    }, 4000);
  }

  triggerFileDownload() {
    const link = document.createElement('a');
    link.href = this.pdfPath;
    link.download = 'rise-of-ai-agent.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
